/* eslint-disable react/jsx-props-no-spreading */
import { FolderBrowserView, GetInteractivity, LibraryOverflowPopover, NodeComponent, useTreeBrowser, useTreeBrowserDragAndDrop } from "@iventis/tree-browser";
import { Content } from "@iventis/translations/content/typed-content";
import { CustomDialog, SimpleTooltip } from "@iventis/components";
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { StyledIconButton, iconButtonSize, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { StopPropagationComponent, useFunctionality, useReadWritePermissionCheck } from "@iventis/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { inlineTextIconMargin } from "@iventis/styles/src/atomic-rules";
import { Checkbox, useTheme } from "@mui/material";
import { FlatLibraryNode, LibraryNodeProps } from "@iventis/tree-browser/implementations/library/types/library-node.types";
import { LibraryListNodeComponent } from "@iventis/tree-browser/implementations/library/components/library-list-node";
import { NodeWithHierarchy, TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { PermissionsEditModalComponent } from "@iventis/permissions/src/permissions-edit-modal";
import { useHistory } from "@iventis/subscriptions";
import { RenameNodeComponent } from "./rename-node";
import { LibraryTileNodeComponent } from "./library-tile-node";
import { LibraryTileNodePreview } from "./library-tile-node-preview";
import globe from "../assets/globe.jpg";
import { navigateToMap } from "../map.helpers";
import { StandardNodeContent } from "./library-node-content";
import { DeleteNodesConfirmationComponent } from "./delete-nodes-confirmation";
import { BottomUpBreadcrumbComponent, getLibraryFromTreeWhereBottomUpHierarchyIsPresent } from "./bottom-up-breadcrumb";
import { createHrefToNode, isStaticLibrary, StaticLibraryId } from "./mapping-library.helpers";
import { LibraryCopyDialog } from "./library-copy-dialog";

enum NodeModal {
    "edit",
    "permissions",
    "delete",
    "copy",
}

export type LibraryNodeExtraProps = {
    viewType: FolderBrowserView;
    contentClassName?: string;
    draggingNode?: boolean;
    isDragging?: boolean;
    rootLibraryName?: string;
    disableActions?: boolean;
    /** Used to shorten the node content if we are embedded in a modal
     * Ideally, we would not need this and should use container queries on the components within node-content,
     * but styled-components doesn't support it currently.
     */
    embedded?: boolean;
    nodesBeingEdited?: string[];
};

const RootLibraryNodeComponent: NodeComponent<FlatLibraryNode, LibraryNodeExtraProps> = ({ node, extraProps: { viewType, draggingNode, isDragging } }) => {
    const translate = useIventisTranslate();

    const theme = useTheme<Theme>();

    const props: LibraryNodeProps = useMemo(
        () => ({
            text: {
                emptyMessage: translate(Content.library.empty_node, { nodeName: node.name }),
                screenReaderOverflowMessage: translate(Content.library.overflow.description, { nodeName: node.name }),
            },
            node,
            expandable: true,
            preview: libraryListIcons(theme)[node.type],
            draggingNode,
            isDragging,
            isNameClickable: false,
        }),
        [translate, node, node.type, draggingNode, isDragging]
    );
    return (
        <>
            {viewType === FolderBrowserView.List && <LibraryListNodeComponent {...props} />}
            {viewType === FolderBrowserView.Tile && <LibraryTileNodeComponent {...props} />}
        </>
    );
};

export const FavouriteButtonComponent: FunctionComponent<{ checked: boolean; onChange: (checked: boolean) => void; className: string }> = ({
    checked,
    onChange,
    className = "favourite",
}) => {
    const theme = useTheme<Theme>();
    return (
        <Checkbox
            checked={checked}
            style={{ display: "flex", alignItems: "center" }}
            onDoubleClick={(e) => e.stopPropagation()}
            onClick={(e) => {
                e.stopPropagation();
                onChange(!checked);
            }}
            icon={<FontAwesomeIcon style={{ color: theme.shades.two }} icon={["fal", "star"]} />}
            checkedIcon={<FontAwesomeIcon style={{ color: theme.shades.two }} icon={["fas", "star"]} />}
            className={className}
        />
    );
};

const PadlockButtonComponent: FunctionComponent<{ node: Node }> = ({ node }) => {
    const theme = useTheme<Theme>();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [overflowOpen, setOverflowOpen] = useState(false);
    const translate = useIventisTranslate();
    return (
        <>
            <SimpleTooltip placement="top" text={translate(Content.library2.permissions.no_read_permissions)}>
                <StyledIconButton
                    style={{ width: iconButtonSize, height: iconButtonSize }}
                    ref={buttonRef}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOverflowOpen(true);
                    }}
                >
                    <FontAwesomeIcon style={{ color: theme.shades.two }} icon={["fal", "lock"]} size="xs" />
                </StyledIconButton>
            </SimpleTooltip>
            <LibraryOverflowPopover
                overflowButtonRef={buttonRef}
                overflowOptions={[{ label: `${translate(Content.library2.permissions.request_access)} ${translate(Content.common.in_development)}`, selected: () => undefined }]}
                overflowOpen={overflowOpen}
                setOverflowOpen={setOverflowOpen}
                node={node}
            />
        </>
    );
};

const FolderNodeComponent: NodeComponent<FlatLibraryNode, LibraryNodeExtraProps> = ({
    node,
    parentNodeType,
    extraProps: { viewType, contentClassName, draggingNode, isDragging, disableActions, embedded, nodesBeingEdited },
    getInteractivity,
}) => {
    const [{ expandedNodeIds, treeId, tree, singleSelectionOnly, selectedNodeIds }, treeBrowserContext] = useTreeBrowser(
        TreeBrowserDomainNodeContext,
        node.id,
        "expandedNodeIds",
        "selectedNodeIds",
        "treeId",
        "tree",
        "singleSelectionOnly"
    );

    const history = useHistory();
    const expanded = expandedNodeIds.includes(node.id);

    const translate = useIventisTranslate();
    const functionality = useFunctionality();

    const [openModal, setOpenModal] = useState<NodeModal>(undefined);

    const nodeName = translate(Content.library.node_names.Folder);

    const { disabled, selectable, draggable, isNameClickable }: ReturnType<typeof getInteractivity> = useMemo(() => handleGetInteractivity(getInteractivity, node), [
        node,
        getInteractivity,
    ]);

    const [setRefs, attributes, listeners] = useTreeBrowserDragAndDrop(node, draggingNode, draggable && parentNodeType !== NodeType.Map);

    const theme = useTheme<Theme>();

    const [hasReadPermissions, hasEditPermissions] = useReadWritePermissionCheck(node?.authorisation);

    const getPreview = useMemo(() => {
        switch (viewType) {
            case FolderBrowserView.List:
                return libraryListIcons(theme, expanded)[node.type];
            case FolderBrowserView.Tile:
                if (!hasReadPermissions) {
                    return <PadlockPreview nodeType={node.type} />;
                }
                return <LibraryTileNodePreview childCount={node.childCount} />;
            default:
                throw new Error(`View type not recognised: ${viewType}`);
        }
    }, [viewType, expanded, node, hasReadPermissions]);

    const setFavourite = (value) => {
        treeBrowserContext.toggleFavourite(node, value);
    };

    const openCopyModal = () => (functionality.plansCopying ? setOpenModal(NodeModal.copy) : functionality.open?.("plansCopying"));

    const props: LibraryNodeProps = useMemo(
        () => ({
            node: node as Node,
            text: {
                emptyMessage: translate(Content.library.empty_node, { nodeName: node.name }),
                screenReaderOverflowMessage: translate(Content.library.overflow.description, { nodeName: node.name }),
            },
            subtitle: node?.bottomUpHierarchy && (
                <BottomUpBreadcrumbComponent
                    bottomUpHierarchy={node.bottomUpHierarchy}
                    endingParents={viewType === FolderBrowserView.Tile ? 1 : 2}
                    translatableContent={Content.library2.mappingLibraries}
                />
            ),
            preview: getPreview,
            expandable: true,
            open: () => {
                if (isStaticLibrary(treeId)) {
                    history.push(createHrefToNode(getLibraryFromTreeWhereBottomUpHierarchyIsPresent(tree, node.id).id, node.id));
                } else {
                    if (singleSelectionOnly) {
                        if (hasEditPermissions && (!selectable.disabled || node.id === tree.id)) {
                            treeBrowserContext.toggleSelectNodes([node], undefined);
                        } else {
                            treeBrowserContext.toggleSelectNodes(selectedNodeIds, undefined);
                        }
                    }
                    treeBrowserContext.setMainNode(node);
                }
            },
            overflowOptions:
                disableActions || !hasReadPermissions
                    ? []
                    : !hasEditPermissions
                    ? [
                          {
                              label: translate(Content.library2.copy_node.button, { nodeType: nodeName }),
                              selected: openCopyModal,
                          },
                      ]
                    : [
                          {
                              label: translate(Content.library.update_node.title, { nodeName }),
                              selected: () => setOpenModal(NodeModal.edit),
                          },
                          {
                              label: translate(Content.library2.copy_node.button, { nodeType: nodeName }),
                              selected: openCopyModal,
                          },
                          {
                              label: translate(Content.library.permissions, { nodeName }),
                              selected: () => setOpenModal(NodeModal.permissions),
                          },
                          {
                              label: translate(Content.library.delete_node.singular_confirm_button, { nodeType: nodeName }),
                              selected: () => setOpenModal(NodeModal.delete),
                              disabled: nodesBeingEdited && nodesBeingEdited.includes(node.id),
                          },
                      ],
            content: (
                <>
                    {/* Favourites component already shown in the tile view */}
                    {viewType === FolderBrowserView.List &&
                        (hasReadPermissions ? (
                            <FavouriteButtonComponent checked={node.favourite} onChange={setFavourite} className="favourite" />
                        ) : (
                            <PadlockButtonComponent node={node} />
                        ))}
                    {hasReadPermissions &&
                        (treeId === StaticLibraryId.RecentlyViewed ? (
                            <StandardNodeContent
                                tile={viewType === FolderBrowserView.Tile}
                                className={contentClassName}
                                viewedAt={node.lastViewedAt}
                                updatedAt={node.lastUpdatedAt}
                            />
                        ) : (
                            <StandardNodeContent
                                tile={viewType === FolderBrowserView.Tile}
                                className={contentClassName}
                                createdAt={!embedded && node.createdAt}
                                updatedAt={node.lastUpdatedAt}
                            />
                        ))}
                </>
            ),
            disabled: disabled || !hasReadPermissions,
            selectable,
            draggingNode,
            disableActions,
            isDragging,
            setFavourite,
            isNameClickable,
        }),
        [node, node.favourite, getPreview, disabled, selectable, draggingNode, translate, isDragging, treeBrowserContext, isNameClickable, hasEditPermissions, hasReadPermissions]
    );

    return (
        <div ref={setRefs} {...attributes} {...listeners} style={{ position: "relative" }} data-testid={`library-node-${node?.name}`}>
            {viewType === FolderBrowserView.List && <LibraryListNodeComponent {...props} />}
            {viewType === FolderBrowserView.Tile && <LibraryTileNodeComponent {...props} />}
            <StopPropagationComponent>
                <CustomDialog maxWidth="sm" open={openModal === NodeModal.edit} fullWidth onClose={() => setOpenModal(undefined)}>
                    {openModal === NodeModal.edit && <RenameNodeComponent nodeToUpdate={node as Node} handleModalClose={() => setOpenModal(undefined)} />}
                </CustomDialog>
                <DeleteNodesConfirmationComponent modalLocation="library" nodes={[node as Node]} onClose={() => setOpenModal(undefined)} show={openModal === NodeModal.delete} />
                <PermissionsEditModalComponent node={node as Node} close={() => setOpenModal(undefined)} open={openModal === NodeModal.permissions} />
                {openModal === NodeModal.copy && (
                    <LibraryCopyDialog
                        isOpen={openModal === NodeModal.copy}
                        closeModal={() => setOpenModal(undefined)}
                        treeId={treeId}
                        onConfirm={(nodes, copyId, newParentId, includeMapObjects, keepPermissions) => {
                            treeBrowserContext.copyNodes(nodes as NodeWithHierarchy[], copyId, newParentId, includeMapObjects, keepPermissions);
                            treeBrowserContext.toggleSelectNodes(nodes, undefined);
                        }}
                        nodesToCopy={[node as Node]}
                        canSwitchLibrary
                    />
                )}
            </StopPropagationComponent>
        </div>
    );
};

const MapNodeComponent: NodeComponent<FlatLibraryNode, LibraryNodeExtraProps> = ({
    node,
    extraProps: { viewType, contentClassName, draggingNode, isDragging, disableActions, embedded, nodesBeingEdited },
    getInteractivity,
}) => {
    const translate = useIventisTranslate();
    const functionality = useFunctionality();

    const history = useHistory();

    const [openModal, setOpenModal] = useState<NodeModal>(undefined);

    const nodeName = useMemo(() => translate(Content.library.node_names.Map), []);

    const { disabled, selectable, draggable, isNameClickable }: ReturnType<typeof getInteractivity> = useMemo(() => handleGetInteractivity(getInteractivity, node), [
        node,
        getInteractivity,
    ]);

    const [{ nodeThumbnails, treeId }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "nodeThumbnails", "treeId");

    useEffect(() => {
        treeBrowserContext.getThumbnail(node);
    }, []);

    const [setRefs, attributes, listeners] = useTreeBrowserDragAndDrop(node, draggingNode, draggable);

    const [hasReadPermissions, hasEditPermissions] = useReadWritePermissionCheck(node?.authorisation);

    const theme = useTheme<Theme>();

    const thumbnailUrl = nodeThumbnails[node?.id];
    const getPreview = useMemo(() => {
        switch (viewType) {
            case FolderBrowserView.List:
                return libraryListIcons(theme)[node.type];
            case FolderBrowserView.Tile:
                if (!hasReadPermissions) {
                    return <PadlockPreview nodeType={node.type} />;
                }
                return thumbnailUrl ? <MapThumbnailPreview backgroundImageUrl={thumbnailUrl} /> : <MapThumbnailPreview backgroundImageUrl={globe} />;
            default:
                throw new Error(`View type not recognised: ${viewType}`);
        }
    }, [viewType, thumbnailUrl, node, hasReadPermissions]);

    const setFavourite = (value) => {
        treeBrowserContext.toggleFavourite(node, value);
    };

    const openCopyModal = () => (functionality.plansCopying ? setOpenModal(NodeModal.copy) : functionality.open?.("plansCopying"));

    const props: LibraryNodeProps = useMemo(
        () => ({
            setFavourite,
            subtitle: node?.bottomUpHierarchy && (
                <BottomUpBreadcrumbComponent
                    bottomUpHierarchy={node.bottomUpHierarchy}
                    endingParents={viewType === FolderBrowserView.Tile ? 1 : 2}
                    translatableContent={Content.library2.mappingLibraries}
                />
            ),
            content: (
                <>
                    {/* Favourites component already shown in the tile view */}
                    {viewType === FolderBrowserView.List &&
                        (hasReadPermissions ? (
                            <FavouriteButtonComponent checked={node.favourite} onChange={setFavourite} className="favourite" />
                        ) : (
                            <PadlockButtonComponent node={node} />
                        ))}
                    {hasReadPermissions &&
                        (treeId === StaticLibraryId.RecentlyViewed && hasReadPermissions ? (
                            <StandardNodeContent
                                tile={viewType === FolderBrowserView.Tile}
                                className={contentClassName}
                                viewedAt={node.lastViewedAt}
                                updatedAt={node.lastUpdatedAt}
                            />
                        ) : (
                            <StandardNodeContent
                                tile={viewType === FolderBrowserView.Tile}
                                className={contentClassName}
                                createdAt={!embedded && node.createdAt}
                                updatedAt={node.lastUpdatedAt}
                            />
                        ))}
                </>
            ),
            preview: getPreview,
            text: {
                emptyMessage: translate(Content.library.empty_node, { nodeName: node.name }),
                screenReaderOverflowMessage: translate(Content.library.overflow.description, { nodeName: node.name }),
            },
            node,
            expandable: true,
            overflowOptions:
                disableActions || !hasReadPermissions
                    ? []
                    : !hasEditPermissions
                    ? [
                          {
                              label: translate(Content.library2.copy_node.button, { nodeType: nodeName }),
                              selected: openCopyModal,
                          },
                      ]
                    : [
                          {
                              label: translate(Content.library.update_node.title, { nodeName }),
                              selected: () => setOpenModal(NodeModal.edit),
                          },
                          {
                              label: translate(Content.library2.copy_node.button, { nodeType: nodeName }),
                              selected: openCopyModal,
                          },
                          {
                              label: translate(Content.library.permissions, { nodeName }),
                              selected: () => setOpenModal(NodeModal.permissions),
                          },
                          {
                              label: translate(Content.library.delete_node.singular_confirm_button, { nodeType: nodeName }),
                              selected: () => setOpenModal(NodeModal.delete),
                              disabled: nodesBeingEdited && nodesBeingEdited.includes(node.id),
                          },
                      ],
            open: () => navigateToMap(node.id, node.parentId, history),
            disabled: disabled || !hasReadPermissions,
            selectable,
            disableActions,
            draggingNode,
            isDragging,
            isNameClickable,
        }),
        [history, node, thumbnailUrl, draggingNode, translate, disabled, selectable, getPreview, isDragging, isNameClickable, hasEditPermissions, hasReadPermissions]
    );

    return (
        <div ref={setRefs} {...attributes} {...listeners} style={{ position: "relative" }} data-testid={`library-node-${node?.name}`}>
            {viewType === FolderBrowserView.List && <LibraryListNodeComponent {...props} />}
            {viewType === FolderBrowserView.Tile && <LibraryTileNodeComponent {...props} />}
            <StopPropagationComponent>
                <CustomDialog maxWidth="sm" open={openModal === NodeModal.edit} fullWidth onClose={() => setOpenModal(undefined)}>
                    {openModal === NodeModal.edit && <RenameNodeComponent nodeToUpdate={node as Node} handleModalClose={() => setOpenModal(undefined)} />}
                    <span> </span>
                </CustomDialog>
                <DeleteNodesConfirmationComponent modalLocation="library" nodes={[node as Node]} onClose={() => setOpenModal(undefined)} show={openModal === NodeModal.delete} />
                <PermissionsEditModalComponent node={node as Node} close={() => setOpenModal(undefined)} open={openModal === NodeModal.permissions} />
                {openModal === NodeModal.copy && (
                    <LibraryCopyDialog
                        isOpen={openModal === NodeModal.copy}
                        closeModal={() => setOpenModal(undefined)}
                        treeId={treeId}
                        onConfirm={(nodes, copyId, newParentId, includeMapObjects, keepPermissions) => {
                            treeBrowserContext.copyNodes(nodes as NodeWithHierarchy[], copyId, newParentId, includeMapObjects, keepPermissions);
                            treeBrowserContext.toggleSelectNodes(nodes, undefined);
                        }}
                        nodesToCopy={[node as Node]}
                        canSwitchLibrary
                    />
                )}
            </StopPropagationComponent>
        </div>
    );
};

const LayerNodeComponent: NodeComponent<FlatLibraryNode, LibraryNodeExtraProps> = ({
    node,
    extraProps: { viewType, contentClassName, isDragging, disableActions },
    getInteractivity,
}) => {
    const [{ nodeThumbnails }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "nodeThumbnails", "tree");

    useEffect(() => {
        treeBrowserContext.getThumbnail(node);
    }, []);

    const { disabled, selectable, isNameClickable }: ReturnType<typeof getInteractivity> = useMemo(() => handleGetInteractivity(getInteractivity, node), [node, getInteractivity]);

    const translate = useIventisTranslate();

    const [hasReadPermissions, hasEditPermissions] = useReadWritePermissionCheck(node?.authorisation);

    const [setRefs] = useTreeBrowserDragAndDrop(node, false, hasEditPermissions);

    const theme = useTheme<Theme>();

    // Memoise image as it will technically be a new object every render and therefore re run the props useMemo
    const thumbnailURL = nodeThumbnails[node?.id];
    const image = useMemo(
        () =>
            thumbnailURL ? (
                <img style={{ height: "20px", width: "20px", marginRight: inlineTextIconMargin, objectFit: "contain" }} src={nodeThumbnails[node?.id]} alt={node?.name} />
            ) : (
                libraryListIcons(theme)[node.type]
            ),
        [node, thumbnailURL]
    );

    const props: LibraryNodeProps = useMemo(
        () => ({
            text: {
                emptyMessage: translate(Content.library.empty_node, { nodeName: node.name }),
                screenReaderOverflowMessage: translate(Content.library.overflow.description, { nodeName: node.name }),
            },
            content: hasReadPermissions && (
                <StandardNodeContent tile={viewType === FolderBrowserView.Tile} className={contentClassName} createdAt={node.createdAt} updatedAt={node.lastUpdatedAt} />
            ),
            node: node as Node,
            preview: image,
            expandable: false,
            overflowOptions: [],
            selectable,
            disabled: disabled || !hasReadPermissions,
            isDragging,
            disableActions,
            isNameClickable,
        }),
        [node, selectable, disabled, image, translate, isDragging, isNameClickable, hasEditPermissions]
    );

    return (
        <div ref={setRefs} style={{ position: "relative" }} data-testid={`library-node-${node?.name}`}>
            {viewType === FolderBrowserView.List && <LibraryListNodeComponent {...props} />}
            {viewType === FolderBrowserView.Tile && <LibraryTileNodeComponent {...props} />}
        </div>
    );
};

export const libraryNodeComponents = {
    [NodeType.Folder]: FolderNodeComponent,
    [NodeType.Group]: FolderNodeComponent,
    [NodeType.Map]: MapNodeComponent,
    [NodeType.Layer]: LayerNodeComponent,
    [NodeType.MappingLibrary]: RootLibraryNodeComponent,
};

// Same as the StyledGhostChildCount, except this can also be expanded for map nodes too
export const libraryListIcons = (theme: Theme, expanded = false) => ({
    [NodeType.Folder]: expanded ? (
        <FontAwesomeIcon icon={["fas", "folder-open"]} style={{ color: theme.shades.two }} />
    ) : (
        <FontAwesomeIcon icon={["fas", "folder-closed"]} style={{ color: theme.shades.two }} />
    ),
    [NodeType.Group]: <FontAwesomeIcon icon={["fas", "folder-closed"]} style={{ color: theme.shades.two }} />,
    [NodeType.Map]: <FontAwesomeIcon icon={["far", "map"]} style={{ color: theme.shades.two }} />,
    [NodeType.Layer]: <FontAwesomeIcon icon={["far", "layer-group"]} style={{ color: theme.shades.two }} />,
});

export const libraryNodeTypeFontawesome = {
    [NodeType.Folder]: ["fas", "folder-closed"],
    [NodeType.Group]: ["fas", "folder-closed"],
    [NodeType.Map]: ["far", "map"],
    [NodeType.Layer]: ["far", "layer-group"],
    [NodeType.MappingLibrary]: ["fas", "folder"],
};

const handleGetInteractivity = (getInteractivity: GetInteractivity<Node>, node: Node) =>
    getInteractivity == null ? { disabled: false, selectable: { disabled: false, tooltip: undefined }, draggable: true, isNameClickable: true } : getInteractivity(node);

const PadlockPreview: FunctionComponent<{ nodeType: NodeType }> = ({ nodeType }) => {
    const theme = useTheme<Theme>();
    return nodeType === NodeType.Folder ? (
        <>
            <FontAwesomeIcon style={{ color: theme.shades.five, position: "absolute", bottom: "20px", right: "65px" }} icon={["fas", "lock"]} size="1x" />
            <FontAwesomeIcon icon={["fas", "folder-closed"]} size="6x" />
        </>
    ) : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.shades.two }}>
            <FontAwesomeIcon style={{ color: theme.shades.five }} icon={["fas", "lock"]} size="2x" />
        </div>
    );
};

export const MapThumbnailPreview = styled.div<{ backgroundImageUrl: string }>`
    width: 100%;
    height: 100%;
    background-image: url(${({ backgroundImageUrl }) => backgroundImageUrl});
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
`;
